import { z } from 'zod';
import { ApiError, apiClient, prepareApiRequest } from './client';

/**
 * The client's no-login pages reached by a secure link (T-0191, over the
 * T-0174 gate and the T-0175, T-0182 and T-0183 public handlers).
 *
 * Contract: `apps/api/src/routes/secure-links.ts`. Everything is under
 * `/api/v1/secure-links`, with no Entra token: the link itself is the
 * credential, sent in the `X-Caliber-Link` header and never in a URL, so it
 * never reaches a server log (E12-F08 §2). Every refusal is the same 401
 * `link.invalid`, whatever the reason; the page says the link is no longer
 * valid and nothing more.
 */

export const SECURE_LINK_HEADER = 'X-Caliber-Link';
const BASE = '/api/v1/secure-links';

const AnswerValueSchema = z.union([
  z.object({ text: z.string() }),
  z.object({ rating: z.number() }),
  z.object({ option: z.string() }),
  z.object({ options: z.array(z.string()) }),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

export const QuestionnairePayloadSchema = z.object({
  title: z.string(),
  audience_line: z.string().nullable(),
  minutes: z.number().nullable(),
  one_per_department: z.boolean(),
  questions: z.array(
    z.object({
      run_question_id: z.string(),
      ordinal: z.number(),
      prompt: z.string(),
      answer_type: z.string(),
      options: z.array(z.string()),
    }),
  ),
  draft_values: z.record(AnswerValueSchema),
  answered: z.number(),
});
export type QuestionnairePayload = z.infer<typeof QuestionnairePayloadSchema>;

export const MapValidationPayloadSchema = z.object({
  title: z.string(),
  version_no: z.number(),
  bpmn_xml: z.string(),
  lanes: z.array(z.object({ key: z.string(), name: z.string() })),
  steps: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      type: z.string(),
      lane: z.string(),
      system: z.string().nullable(),
      confirmation: z.enum(['confirmed', 'unconfirmed']).nullable(),
    }),
  ),
  unconfirmed_steps: z.number(),
  validator: z.object({ full_name: z.string(), title: z.string().nullable() }),
  answer: z
    .object({
      outcome: z.enum(['validated', 'changes_requested']),
      answered_at: z.string(),
    })
    .nullable(),
});
export type MapValidationPayload = z.infer<typeof MapValidationPayloadSchema>;

export const SignoffPayloadSchema = z.object({
  state: z.enum(['requested', 'signed', 'waived', 'changes_requested', 'reopened', 'cancelled']),
  requested_by: z.string().nullable(),
  message: z.string().nullable(),
  signatory: z.object({
    full_name: z.string(),
    title: z.string().nullable(),
    email: z.string(),
  }),
  /** Counted by the server from the frozen packet. */
  summary: z.object({
    requirements: z.number(),
    modules: z.number(),
    critical: z.number(),
    people_heard: z.number(),
    areas: z.number(),
  }),
  /** Fetched with the link header, like every other call: never a plain link. */
  packet_url: z.string(),
  signed: z.object({ name: z.string(), at: z.string() }).nullable(),
});
export type SignoffPayload = z.infer<typeof SignoffPayloadSchema>;

const SessionBase = {
  client_name: z.string(),
  engagement_name: z.string(),
  person_first_name: z.string(),
  expires_at: z.string(),
  completed: z.boolean(),
};

export const SecureLinkSessionSchema = z.discriminatedUnion('purpose', [
  z.object({
    ...SessionBase,
    purpose: z.literal('questionnaire'),
    payload: QuestionnairePayloadSchema,
  }),
  z.object({
    ...SessionBase,
    purpose: z.literal('map_validation'),
    payload: MapValidationPayloadSchema,
  }),
  z.object({ ...SessionBase, purpose: z.literal('signoff'), payload: SignoffPayloadSchema }),
]);
export type SecureLinkSession = z.infer<typeof SecureLinkSessionSchema>;

/** Every public call: the link in its header, no bearer, nothing in the URL. */
function linkCall<T>(
  token: string,
  path: string,
  schema: z.ZodType<T>,
  method: 'GET' | 'PUT' | 'POST' = 'GET',
  body?: unknown,
): Promise<T> {
  return apiClient(`${BASE}${path}`, schema, {
    method,
    noAuth: true,
    headers: { [SECURE_LINK_HEADER]: token },
    ...(body === undefined ? {} : { body }),
  });
}

/**
 * What the link is for, and that purpose's view. Read loosely here: the
 * page parses it with the purpose's own schema (Discovery's above, or the
 * vendor upload's, T-0207).
 */
export function getSecureLinkSession(
  token: string,
): Promise<{ purpose: string } & Record<string, unknown>> {
  return linkCall(token, '/session', z.object({ purpose: z.string() }).passthrough());
}

/** "Saves as you go": replaces what is saved on the run; nothing becomes evidence. */
export function saveQuestionnaireDraft(
  token: string,
  values: Record<string, AnswerValue>,
): Promise<{ saved_at: string }> {
  return linkCall(
    token,
    '/questionnaire/draft',
    z.object({ saved_at: z.string() }).passthrough(),
    'PUT',
    { values },
  );
}

/**
 * Send: the answers become evidence now (Decision 54.27). `null` says a
 * question has no answer, whatever was saved for it before.
 */
export function submitQuestionnaire(
  token: string,
  values: Record<string, AnswerValue | null>,
): Promise<{ answered: number }> {
  return linkCall(token, '/questionnaire:submit', z.object({ answered: z.number() }), 'POST', {
    values,
  });
}

const MapAnswerSchema = z.object({
  outcome: z.enum(['validated', 'changes_requested']),
  version_no: z.number(),
  answered_at: z.string(),
  confirmed_steps: z.number(),
});
export type MapAnswer = z.infer<typeof MapAnswerSchema>;

/** "This is how we work": the typed name must match the person the link was sent to. */
export function validateMap(
  token: string,
  body: { typed_name: string; comment?: string },
): Promise<MapAnswer> {
  return linkCall(token, '/map:validate', MapAnswerSchema, 'POST', {
    confirmed: true,
    typed_name: body.typed_name,
    ...(body.comment ? { comment: body.comment } : {}),
  });
}

/** "Send my comments": the map stays a draft. */
export function requestMapChanges(token: string, comment: string): Promise<MapAnswer> {
  return linkCall(token, '/map:request-changes', MapAnswerSchema, 'POST', { comment });
}

/**
 * The packet PDF the signatory is asked to sign, fetched with the link
 * header (the packet of this sign-off only). While it is still being
 * rendered the server answers 409 `signoff.packet_not_ready`.
 */
export async function getSignoffPacket(token: string, packetUrl: string): Promise<Blob> {
  // Only ever our own API path: the URL comes from the session, never from elsewhere.
  const path = packetUrl.startsWith(`${BASE}/`) ? packetUrl : `${BASE}/signoff/packet`;
  const { url, headers, correlationId } = await prepareApiRequest(path, {
    noAuth: true,
    headers: { [SECURE_LINK_HEADER]: token, Accept: 'application/pdf' },
  });
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'internal',
      body.error?.message ?? 'The packet could not be opened.',
      correlationId,
      body.error?.details,
    );
  }
  return res.blob();
}

/** Sign: attested, with the typed name matching the signatory's person record. */
export function signRequirements(token: string, typedName: string): Promise<{ signed_at: string }> {
  return linkCall(
    token,
    '/signoff:sign',
    z.object({ signed_at: z.string() }).passthrough(),
    'POST',
    {
      attested: true,
      typed_name: typedName,
    },
  );
}

export function requestSignoffChanges(token: string, comment: string): Promise<unknown> {
  return linkCall(token, '/signoff:request-changes', z.unknown(), 'POST', { comment });
}
